import React, { useEffect } from 'react';
import { safeArray } from '../../utils/safe';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PlusCircle, User, Pencil, Trash, QrCode, LayoutDashboard } from 'lucide-react';
import { toast } from 'react-toastify';
import type { Table } from '../../types/@types/table';
import {
  getTables,
  createTable,
  updateTable,
  deleteTable,
} from '../../services/TableService';

interface TableForm {
  number: number;
  capacity: number;
  status: Table['status'];
}

const schema = yup.object({
  number: yup.number().required().min(1),
  capacity: yup.number().required().min(1),
  status: yup.mixed<Table['status']>().oneOf([
    'Disponible',
    'Ocupada',
    'Reservada',
  ]),
});

const statusLabels: Record<Table['status'], string> = {
  Disponible: 'Disponible',
  Ocupada: 'Ocupada',
  Reservada: 'Reservada',
};

const statusClasses: Record<Table['status'], string> = {
  Disponible: 'bg-green-100 text-green-800',
  Ocupada: 'bg-red-100 text-red-800',
  Reservada: 'bg-yellow-100 text-yellow-800',
};

function TableModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TableForm) => void;
  initialData?: Table | null;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TableForm>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      number: initialData?.number ?? 0,
      capacity: initialData?.capacity ?? 0,
      status: initialData?.status ?? 'Disponible',
    },
  });

  useEffect(() => {
    reset({
      number: initialData?.number ?? 0,
      capacity: initialData?.capacity ?? 0,
      status: initialData?.status ?? 'Disponible',
    });
  }, [initialData, reset]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {initialData ? `Editar Mesa #${initialData.number}` : 'Crear Nueva Mesa'}
          </h2>
          <button onClick={onClose} className="text-gray-500">✕</button>
        </div>
        <form
          onSubmit={handleSubmit(onSubmit as any)}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium">Número de Mesa</label>
            <input
              type="number"
              {...register('number', { valueAsNumber: true })}
              className="mt-1 w-full border rounded px-3 py-2"
            />
            {errors.number && (
              <p className="text-red-600 text-sm">{errors.number.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium">Capacidad</label>
            <input
              type="number"
              {...register('capacity', { valueAsNumber: true })}
              className="mt-1 w-full border rounded px-3 py-2"
            />
            {errors.capacity && (
              <p className="text-red-600 text-sm">{errors.capacity.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium">Estado</label>
            <select
              {...register('status')}
              className="mt-1 w-full border rounded px-3 py-2"
            >
              <option value="Disponible">Disponible</option>
              <option value="Ocupada">Ocupada</option>
              <option value="Reservada">Reservada</option>
            </select>
          </div>
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 rounded"
          >
            {initialData ? 'Guardar Cambios' : 'Crear Mesa'}
          </button>
        </form>
      </div>
    </div>
  );
}

function QrModal({
  isOpen,
  onClose,
  table
}: {
  isOpen: boolean;
  onClose: () => void;
  table: Table | null
}) {
  if (!isOpen || !table) return null;

  // Si estamos en un dominio público real, usarlo por defecto; si estamos en localhost/IP, buscar en localStorage
  const isPublicHost = !window.location.hostname.includes('localhost') &&
    !window.location.hostname.startsWith('192.168.') &&
    !window.location.hostname.startsWith('127.0.');

  const [domain, setDomain] = React.useState<string>(() => {
    const saved = localStorage.getItem('dufi_qr_public_domain');
    if (saved) return saved;
    return isPublicHost ? window.location.origin : '';
  });

  const [editingDomain, setEditingDomain] = React.useState(false);

  const baseDomain = domain.trim() || window.location.origin;
  const cleanBase = baseDomain.replace(/\/+$/, '');
  const url = `${cleanBase}/#/menu/${table.id}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(url)}`;

  const handleSaveDomain = (newDomain: string) => {
    const clean = newDomain.trim().replace(/\/+$/, '');
    setDomain(clean);
    localStorage.setItem('dufi_qr_public_domain', clean);
    setEditingDomain(false);
    toast.success('Dominio público guardado para los códigos QR');
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>QR Mesa #${table.number}</title>
            <style>
              body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; text-align: center; padding: 40px; margin: 0; background: #fafafa; }
              .card { background: white; border: 2px solid #e2e8f0; padding: 30px; border-radius: 28px; display: inline-block; box-shadow: 0 10px 25px rgba(0,0,0,0.05); max-width: 380px; }
              .badge { display: inline-block; background: #EEF2FF; color: #4F46E5; padding: 6px 16px; border-radius: 999px; font-weight: 700; font-size: 14px; margin-bottom: 12px; }
              h1 { color: #0F172A; margin: 8px 0 4px 0; font-size: 28px; font-weight: 900; }
              p.sub { color: #64748B; margin: 0 0 20px 0; font-size: 14px; }
              .qr-box { padding: 15px; background: #F8FAFC; border-radius: 20px; display: inline-block; border: 1px solid #E2E8F0; }
              p.footer { color: #94A3B8; font-size: 12px; margin-top: 20px; word-break: break-all; }
              .steps { text-align: left; background: #F8FAFC; padding: 12px 18px; border-radius: 14px; margin-top: 18px; font-size: 12.5px; color: #334155; }
              .steps ol { margin: 0; padding-left: 20px; }
            </style>
          </head>
          <body>
            <div class="card">
              <span class="badge">Autoservicio Digital</span>
              <h1>Mesa #${table.number}</h1>
              <p class="sub">Escanea con la cámara de tu celular</p>
              <div class="qr-box">
                <img src="${qrUrl}" width="260" height="260" alt="QR Mesa #${table.number}" />
              </div>
              <div class="steps">
                <ol>
                  <li>Abre la cámara de tu celular.</li>
                  <li>Apunta al código QR.</li>
                  <li>Elige tus helados y postres favoritos.</li>
                  <li>¡Tu pedido llegará a la mesa!</li>
                </ol>
              </div>
              <p class="footer">${url}</p>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 500);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-[32px] p-6 md:p-8 w-full max-w-md shadow-2xl animate-in zoom-in duration-300">
        <div className="flex justify-between items-center mb-4">
          <div>
            <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider">Código QR para Clientes</span>
            <h2 className="text-2xl font-black text-gray-900">Mesa #{table.number}</h2>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors">✕</button>
        </div>

        {/* Alerta si está en localhost/LAN */}
        {!isPublicHost && !domain && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-2xl text-xs text-amber-800">
            <p className="font-bold">⚠️ Estás generando el QR desde tu red local (XAMPP)</p>
            <p className="mt-1">
              Para que los clientes con datos 4G puedan entrar, ingresa tu dominio de Hostinger (ej: <code className="bg-amber-100 px-1 py-0.5 rounded">https://tudominio.com</code>).
            </p>
          </div>
        )}

        <div className="flex flex-col items-center gap-4">
          <div className="p-4 bg-slate-50 rounded-3xl border-2 border-slate-100 shadow-inner">
            <img
              src={qrUrl}
              alt={`QR Mesa ${table.number}`}
              className="w-56 h-56 rounded-xl"
            />
          </div>

          {/* Configuración de Dominio Público */}
          <div className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-3.5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                🌐 Dominio de Acceso Clientes (Hostinger):
              </span>
              <button
                type="button"
                onClick={() => setEditingDomain(!editingDomain)}
                className="text-[11px] font-bold text-indigo-600 hover:underline"
              >
                {editingDomain ? 'Cancelar' : 'Cambiar Dominio'}
              </button>
            </div>

            {editingDomain ? (
              <div className="flex gap-2 mt-2">
                <input
                  type="url"
                  defaultValue={domain}
                  placeholder="https://tudominio.com"
                  id="qr-domain-input"
                  className="flex-1 text-xs px-3 py-2 bg-white border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => {
                    const inp = document.getElementById('qr-domain-input') as HTMLInputElement;
                    if (inp) handleSaveDomain(inp.value);
                  }}
                  className="px-3 py-2 bg-indigo-600 text-white text-xs font-bold rounded-xl hover:bg-indigo-700"
                >
                  Guardar
                </button>
              </div>
            ) : (
              <p className="text-xs text-slate-600 font-mono break-all bg-white px-3 py-2 rounded-xl border border-slate-200">
                {url}
              </p>
            )}
          </div>

          <div className="flex gap-3 w-full mt-2">
            <button
              onClick={handlePrint}
              className="flex-1 bg-indigo-600 text-white py-3 rounded-2xl font-bold shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              <span>🖨️ Imprimir Cartel QR</span>
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(url);
                toast.success('Enlace de la mesa copiado al portapapeles');
              }}
              className="px-5 bg-slate-100 text-slate-700 py-3 rounded-2xl font-bold hover:bg-slate-200 transition-all active:scale-95"
            >
              Copiar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Tables() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: tables } = useQuery({ queryKey: ['tables'], queryFn: getTables });

  const createMutation = useMutation({
    mutationFn: createTable,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      toast.success('Mesa creada');
    },
    onError: () => toast.error('Error al crear la mesa'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Omit<Table, 'id'> }) =>
      updateTable(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      toast.success('Mesa actualizada');
    },
    onError: () => toast.error('Error al actualizar la mesa'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTable,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      toast.success('Mesa eliminada');
    },
    onError: () => toast.error('Error al eliminar la mesa'),
  });

  const [isOpen, setIsOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Table | null>(null);
  const [qrModal, setQrModal] = React.useState<Table | null>(null);

  const openNew = () => {
    setEditing(null);
    setIsOpen(true);
  };

  const handleEdit = (table: Table) => {
    setEditing(table);
    setIsOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('¿Eliminar mesa?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (data: TableForm) => {
    if (editing) {
      updateMutation.mutate({ id: editing.id, data });
    } else {
      createMutation.mutate(data);
    }
    setIsOpen(false);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
           <h1 className="text-3xl font-black text-slate-900 tracking-tight">Gestión de Mesas</h1>
           <p className="text-slate-500 font-medium">Administra tus mesas y códigos QR</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/tables/floor')}
            className="flex items-center gap-2 px-4 py-3 border border-gray-200 rounded-2xl font-semibold text-sm transition-all hover:bg-gray-50"
          >
            <LayoutDashboard size={18} /> Ver plano
          </button>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-2xl font-bold shadow-xl shadow-indigo-100 transition-all hover:bg-indigo-700 active:scale-95"
          >
            <PlusCircle size={20} /> Nueva Mesa
          </button>
        </div>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {safeArray(tables).map((table) => (
          <div
            key={table.id}
            className="group bg-white rounded-[32px] border border-slate-100 shadow-sm p-6 flex flex-col transition-all hover:shadow-xl hover:border-indigo-100 hover:translate-y-[-4px]"
          >
            <div className="flex justify-between items-start mb-4">
               <div className="text-2xl font-black text-slate-900">Mesa #{table.number}</div>
               <button
                  onClick={() => setQrModal(table)}
                  className="p-2.5 rounded-2xl bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                  title="Ver QR"
               >
                  <QrCode size={18} />
               </button>
            </div>

            <div className="text-sm text-slate-400 font-bold flex items-center gap-2 mb-4">
              <User size={16} className="text-indigo-400" /> {table.capacity} Personas
            </div>

            <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-50">
               <span
                className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${
                  statusClasses[table.status]
                }`}
              >
                {statusLabels[table.status]}
              </span>

              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(table)}
                  className="p-2 rounded-xl text-slate-400 hover:bg-indigo-50 hover:text-indigo-600 transition-all"
                >
                  <Pencil size={18} />
                </button>
                <button
                  onClick={() => handleDelete(table.id)}
                  className="p-2 rounded-xl text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-all"
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <TableModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSubmit={handleSubmit}
        initialData={editing}
      />

      <QrModal
        isOpen={!!qrModal}
        onClose={() => setQrModal(null)}
        table={qrModal}
      />
    </div>
  );
}
